//
// # Schematic — Render Tier
//
// The DOM-free render core for a schematic: the wire index, the entity
// collections, display options, annotation graphics, and the SRV/dot-preview
// state the draw path reads. Kept in its own module (no modes/keys/interaction
// imports) so the headless server can build and redraw a schematic without
// pulling in the browser interaction tier. The interaction facade `Schematic`
// (in `schematic.ts`) owns a `SchematicRender` and delegates to it.
//

// Local Imports
import { Result, Context } from "CoreUtils";
import { model, schdata } from "GenAlphaModel";
import type { RenderHost } from "../../render_host";
import { ContentKind } from "../kind";
import { RenderContentBase } from "../base_render";
import { AnnotationsRender, Label } from "../shared";
import { Instance, SchPin } from "./instance";
import { Dot } from "./dot";
import { Dots, type DotAnchor } from "./dots";
import { Wires } from "./wires";
import { DrawTo, SchematicDisplayOptions } from "../../draw";
import { drawSrvArrows, type SrvOverlay } from "./srv_arrows";

export class SchematicRender extends RenderContentBase {
  override readonly contentKind = ContentKind.Schematic;

  // Drawing Elements
  // Each includes a "data-drawing pair", from source `data` to drawn graphics.
  readonly wires = new Wires(this);
  readonly instances = new Set<Instance>();
  readonly pins = new Set<SchPin>();
  readonly labels = new Set<Label>();
  public dots = new Set<Dot>();

  // Display options controlling schematic label visibility.
  // Mutated in-place by toggle methods; the canvas holds a shared reference
  // set once at content-load time via syncDisplayOptions().
  schematicDisplay = SchematicDisplayOptions.default();

  // Entities being placed, not yet committed. Render-only: drawn (see redraw)
  // but kept out of the committed `instances`/`pins` sets that netlist, save,
  // and hit-test iterate, so an in-progress add never leaks. Mirrors Wires.
  readonly provisionalInstances = new Set<Instance>();
  readonly provisionalPins = new Set<SchPin>();

  // Annotation graphics drawer (render tier).
  readonly annotations = new AnnotationsRender(this);

  // SRV arrow overlay, pushed in by the IDE (workspace.srv) when SRV is active;
  // null means no overlay is drawn. See `srv_arrows.ts`. Public so `Schematic`'s
  // SRV methods can set it.
  srvOverlay: SrvOverlay | null = null;

  // Per-dot supporting geometry, captured when a live-preview move mode starts
  // and dropped when it ends, so only the dots that move are hidden during the
  // preview. See `redraw` and `Dots.captureAnchors`. Null whenever no preview is
  // in flight, which is also how the capture is triggered exactly once.
  dotAnchors: Array<DotAnchor> | null = null;

  static new(editor: RenderHost): SchematicRender {
    return new SchematicRender(editor);
  }

  // Build a render tier from the abstract data model — the render-only subset of
  // the interaction facade's `fromData`: entity collections, annotation
  // graphics, inferred dots, and bbox. Design metadata (properties, constraints,
  // pdkId, symbolImpl, layoutImpls, ident) is interaction-tier and skipped; the
  // draw path never reads it.
  static fromData(host: RenderHost, data: schdata.Schematic): SchematicRender {
    const render = new SchematicRender(host);
    for (const instData of data.instances) {
      render.instances.add(new Instance({ data: instData, parent: render }));
    }
    for (const pinSpec of data.pins) {
      render.pins.add(new SchPin({ data: pinSpec, parent: render }));
    }
    for (const segData of data.wireSegments) {
      // `false`: raw insert, no change log (mirrors the facade's load path).
      render.wires.add(render.wires.provisional(segData), false);
    }
    for (const element of data.graphics) {
      render.annotations.graphicsEntity.add(element);
    }
    // Dots are purely visual; always re-infer from wire geometry on load.
    render.dots = Dots.infer(render);
    render.bbox = data.bbox;
    return render;
  }

  // Load a schematic render tier from schema JSON (server render path).
  static async fromSchema(
    host: RenderHost,
    data: schdata.SchematicJson
  ): Promise<Result<SchematicRender>> {
    const sch = await schdata.Schematic.fromSchema(
      Context.background(),
      data,
      host.depot,
      "" // root namespace
    );
    if (sch.err) return sch;
    return Result.Ok(SchematicRender.fromData(host, sch.val));
  }

  // Under composition the render object is the entity-tree root.
  override get root(): this {
    return this;
  }

  // Draw all elements in the schematic.
  override redraw = (to: DrawTo): unknown => {
    // Redraw all our child elements
    // Drawing is "back to front"; elements drawn later are shown "over" earlier ones.
    // Provisional (in-progress) wires are included in wires.all() automatically.
    // Top-level category visibility flags skip whole groups when false; the
    // currently-being-placed "provisional" entity is always drawn so the user
    // can still see what they're adding even with the parent toggle off.
    if (this.schematicDisplay.wires) {
      to.layer((to: DrawTo) => {
        this.wires.all().forEach((e) => e.redraw(to));
      });
    }

    to.layer((to: DrawTo) => {
      if (this.schematicDisplay.instances) {
        this.instances.forEach((e) => e.redraw(to));
      }
      // Provisionals draw regardless of the visibility toggle (actively placing).
      this.provisionalInstances.forEach((e) => e.redraw(to));
    });
    to.layer((to: DrawTo) => {
      if (this.schematicDisplay.pins) {
        this.pins.forEach((e) => e.redraw(to));
      }
      this.provisionalPins.forEach((e) => e.redraw(to));
    });
    to.layer((to: DrawTo) => {
      this.labels.forEach((e) => e.redraw(to));
    });

    // Junction dots and SRV arrows are both derived data, recomputed only when
    // an edit commits (`updateDots`, and a fresh Check & Save run). While a move
    // mode is previewing geometry they can describe positions the schematic no
    // longer has, so they are hidden for the duration rather than drawn
    // somewhere wrong. Committing the move re-infers the dots and redraws.
    //
    // Dots are hidden individually: the anchors captured when the preview began
    // say which wires each dot rests on, so only the dots whose wires have
    // actually moved drop out. A drag in one corner of the schematic leaves the
    // dots in the other corner alone. Arrows have no such per-marker test — any
    // edit invalidates the whole run — so they are hidden wholesale.
    const previewing = this.editor.modeHandler.movesGeometryLive;
    this.dotAnchors = previewing
      ? this.dotAnchors ?? Dots.captureAnchors(this)
      : null;
    const staleDots =
      this.dotAnchors === null ? null : Dots.stale(this.dotAnchors);

    to.layer((to: DrawTo) => {
      this.dots.forEach((e) => {
        if (staleDots?.has(e)) return;
        e.redraw(to);
      });
    });

    // Redraw the annotation graphics
    to.layer((to: DrawTo) => this.annotations.redraw(to));

    // SRV violation arrows on top. The module early-returns when the overlay
    // is gated off (null); passing null while previewing also clears the hit
    // cache, so a hidden arrow cannot be clicked or hovered.
    return to.layer((to: DrawTo) =>
      drawSrvArrows(to, previewing ? null : this.srvOverlay)
    );
  };

  override recalcBbox = (): void => {
    const bboxes: Array<model.Bbox> = [];
    for (const instance of this.instances) {
      bboxes.push(instance.bbox);
    }
    for (const pin of this.pins) {
      bboxes.push(pin.bbox);
    }
    for (const wire of this.wires.all()) {
      bboxes.push(wire.bbox);
    }
    this.bbox =
      bboxes.length > 0 ? model.Bbox.getBboxUnion(bboxes) : model.Bbox.empty();
  };
}
